/**
 * A task's chat — Aaron, Oct 25, note 1.6: "a running task chat to make
 * notes in each [task] that log by the person who typed them and the time
 * and date it was created … an extended flyout on the left of the current
 * fly out".
 *
 * On a computer or an iPad it is a 340 panel standing **to the left of** the
 * Task sheet, opened and closed by the sheet's **Chat · n** button and
 * remembered open or shut on this device (`state.tasks.chat`, on the server —
 * §4.6). A phone has no room beside a whole-screen sheet, so there the chat is
 * the sheet's second level: **‹ Task** goes back, and the task always opens
 * on itself.
 *
 * Each message is its writer's face and name, the day and the time it was
 * written (the server's words, in the viewer's zone), and its plain text —
 * line breaks kept, links clickable, nothing else read as markup. Oldest
 * first, so the newest sits at the foot by the box. The writer's own carry a
 * ⋯ with **Edit message** (it then reads *edited*) and **Remove message**,
 * which takes it off the chat for everyone; the row stays (rule 2).
 *
 * The chat asks again whenever the events stream says something changed
 * (`activity.changed`), so two people on one task see each other write.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { uuidv7 } from 'uuidv7'
import {
  TASK_CHAT_MAX_CHARS, chatButtonWords, chatParts, type TaskChatAnswer, type TaskChatMessage,
} from '@rowted/shared'
import { ConfirmDialog, Popover } from '../../components/overlays.js'
import { PanelSheet } from '../../components/panel-sheet.js'
import { IconChevronLeft } from '../../frame/icons.js'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Face } from '../faces.js'
import { Glyph } from '../glyphs.js'
import { ACTIVITY_CHANGED_EVENT } from '../nav-badge.js'
import { BackLayer, useActivityNav } from '../use-activity-history.js'

/*
 * A link that asks for the chat (`/activity?tab=tasks&task=<id>&chat=1` — the
 * bell's link for a chat message) says so before the sheet opens, and the
 * next chat to mount opens with it.
 */
let askedForChat = false
export function openTaskChatNext(): void { askedForChat = true }

export interface TaskChat {
  answer: TaskChatAnswer | null
  count: number
  error: string | null
  open: boolean
  /** Opened by a press (not remembered from last time): the box takes the caret. */
  pressed: boolean
  setOpen: (open: boolean) => void
  send: (body: string) => Promise<boolean>
  edit: (id: string, body: string) => Promise<boolean>
  remove: (id: string) => Promise<boolean>
}

const messageOf = (cause: unknown, fallback: string): string => (cause instanceof Error ? cause.message : fallback)

/** One task's chat: its messages, whether it is open, and the three things a person does in it. */
export function useTaskChat(taskId: string): TaskChat {
  const { state, change } = useActivity()
  const phone = usePhone()
  const [answer, setAnswer] = useState<TaskChatAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [asked] = useState(() => askedForChat)
  const [pressed, setPressed] = useState(false)
  /*
   * Open is `?chat=1` too — an entry of its own over the task's, so back closes the chat and
   * then the task (Aaron, Oct 26). A phone's chat is that and nothing else; a computer's is also
   * remembered open or shut, and back over it shuts it.
   */
  const nav = useActivityNav()
  const inAddress = nav.get('chat') === '1'
  /* The words a send was tried with and the key it carried: a retry of the same words is the same message. */
  const attempt = useRef<{ body: string; key: string } | null>(null)

  useEffect(() => {
    askedForChat = false
    /* The bell's link opens the chat, and on a computer it then stays open as it would have by hand. */
    if (asked && !phone && !state.tasks.chat) change({ tasks: { chat: true } })
    // Once, on arrival.
  }, [])

  const load = useCallback(() => {
    activityRequest<TaskChatAnswer>(`/activity/tasks/${taskId}/chat`)
      .then((next) => { setAnswer(next); setError(null) })
      .catch((cause: unknown) => setError(messageOf(cause, 'The chat could not open.')))
  }, [taskId])

  useEffect(() => {
    setAnswer(null)
    load()
    let timer: number | null = null
    const soon = () => {
      if (timer !== null) window.clearTimeout(timer)
      timer = window.setTimeout(load, 300)
    }
    window.addEventListener(ACTIVITY_CHANGED_EVENT, soon)
    return () => {
      window.removeEventListener(ACTIVITY_CHANGED_EVENT, soon)
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [load])

  const open = phone ? inAddress : state.tasks.chat || inAddress
  const { push: pushNav, close: closeNav } = nav
  const setOpen = useCallback((next: boolean) => {
    setPressed(next)
    if (!phone) change({ tasks: { chat: next } })
    if (next) pushNav({ chat: '1' })
    else closeNav(['chat'])
  }, [phone, change, pushNav, closeNav])
  const wasInAddress = useRef(inAddress)
  useEffect(() => {
    if (wasInAddress.current === inAddress) return
    wasInAddress.current = inAddress
    if (!phone && state.tasks.chat !== inAddress) change({ tasks: { chat: inAddress } })
  }, [inAddress, phone, state.tasks.chat, change])

  const write = async (run: () => Promise<TaskChatAnswer>, fallback: string): Promise<boolean> => {
    try {
      setAnswer(await run())
      setError(null)
      return true
    } catch (cause) {
      setError(messageOf(cause, fallback))
      return false
    }
  }

  return {
    answer,
    count: answer?.count ?? 0,
    error,
    open,
    pressed,
    setOpen,
    send: async (body) => {
      /* Changed words after a failure are a new request, so a new key — never the old key with other words. */
      if (attempt.current?.body !== body) attempt.current = { body, key: uuidv7() }
      const { key } = attempt.current
      const done = await write(() => activityRequest<TaskChatAnswer>(`/activity/tasks/${taskId}/chat`, {
        method: 'POST', body: { body }, idempotencyKey: key,
      }), 'That message did not send.')
      if (done) attempt.current = null
      return done
    },
    edit: (id, body) => write(() => activityRequest<TaskChatAnswer>(`/activity/tasks/${taskId}/chat/${id}`, {
      method: 'PATCH', body: { body },
    }), 'That did not save.'),
    remove: (id) => write(() => activityRequest<TaskChatAnswer>(`/activity/tasks/${taskId}/chat/${id}/remove`, {
      method: 'POST', body: {},
    }), 'That message could not be removed.'),
  }
}

/** **Chat · 3** in the sheet's head: the panel beside it on a computer, the chat level on a phone. */
export function TaskChatButton({ chat }: { chat: TaskChat }) {
  const phone = usePhone()
  return (
    <button
      type="button"
      aria-pressed={phone ? undefined : chat.open}
      onClick={() => chat.setOpen(!chat.open)}
      className={cn('inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2 text-[12px] font-semibold',
        chat.open && !phone
          ? 'border-accent-line bg-accent-soft text-accent-dark'
          : 'border-line bg-surface text-text-body hover:bg-hover')}
    >
      <Glyph name="bubble" size={14} />
      {chatButtonWords(chat.count)}
    </button>
  )
}

/** *1 message*, *3 messages*, *Nothing yet*. */
export const countWords = (count: number): string =>
  count === 0 ? 'Nothing yet' : count === 1 ? '1 message' : `${count.toLocaleString('en-US')} messages`

/** A message's text: words, and its links as links — never markup. */
export function ChatText({ body }: { body: string }) {
  return (
    <>
      {chatParts(body).map((part, index) => (part.href
        ? (
          <a key={index} href={part.href} target="_blank" rel="noopener noreferrer"
            className="font-medium text-accent underline decoration-accent-line underline-offset-2 hover:decoration-accent">
            {part.text}
          </a>
        )
        : <span key={index}>{part.text}</span>))}
    </>
  )
}

/**
 * The box: Enter sends on a computer and an iPad, Shift + Enter for a new
 * line (Planner's and Teams' keys); a phone's return is a new line and the
 * arrow sends.
 */
function Composer({ onSend, focus }: { onSend: (body: string) => Promise<boolean>; focus: boolean }) {
  const phone = usePhone()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const box = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [text])

  useEffect(() => { if (focus) box.current?.focus() }, [focus])

  const send = async () => {
    const body = text.trim()
    if (!body || busy) return
    setBusy(true)
    const sent = await onSend(body)
    setBusy(false)
    if (sent) setText('')
    box.current?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !phone && !event.nativeEvent.isComposing) {
      event.preventDefault()
      void send()
    }
  }

  const left = TASK_CHAT_MAX_CHARS - text.length
  return (
    <div className="flex items-end gap-2">
      <textarea
        ref={box}
        aria-label="Message"
        rows={1}
        value={text}
        maxLength={TASK_CHAT_MAX_CHARS}
        placeholder="Write a message…"
        onChange={(event) => setText(event.target.value)}
        onKeyDown={onKeyDown}
        /* 34 tall on a computer, the sheet footer's own button height, so the two footers line up; 44 for a thumb. */
        className={cn('block min-w-0 flex-1 resize-none rounded-[10px] border border-line bg-surface px-3 leading-[1.45] text-text outline-none placeholder:text-text-faint focus:border-accent',
          phone ? 'min-h-[44px] py-2.5 text-[15px]' : 'min-h-[34px] py-1.5 text-[13px]')}
      />
      <button
        type="button"
        onClick={() => void send()}
        disabled={!text.trim() || busy}
        className={cn('inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border font-semibold',
          phone ? 'h-11 w-11' : 'h-[34px] px-3.5 text-[12.5px]',
          text.trim() && !busy
            ? 'border-accent bg-accent text-white hover:bg-accent-dark'
            : 'border-line bg-surface text-text-faint')}
        aria-label={phone ? 'Send' : undefined}
      >
        <Glyph name="send" size={phone ? 17 : 14} />
        {!phone && 'Send'}
      </button>
      {left <= 200 && <span className="sr-only">{left} characters left</span>}
    </div>
  )
}

/** One message — the writer's face, name, when, and the words; the writer's own carry a ⋯. */
function Message({ message, onMenu, editing, onSave, onCancel }: {
  message: TaskChatMessage
  onMenu: (anchor: HTMLElement) => void
  editing: boolean
  onSave: (body: string) => Promise<boolean>
  onCancel: () => void
}) {
  const phone = usePhone()
  const more = useRef<HTMLButtonElement>(null)
  const [draft, setDraft] = useState(message.body)
  const box = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (!editing) return
    setDraft(message.body)
    const el = box.current
    if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length) }
  }, [editing, message.body])

  const save = async () => {
    const body = draft.trim()
    if (!body) return
    if (body === message.body) { onCancel(); return }
    if (await onSave(body)) onCancel()
  }

  return (
    <li className="flex gap-2.5 px-4 py-2.5" data-message={message.id}>
      <Face person={message.author} size={28} self={message.mine} className="mt-px" />
      <div className="min-w-0 flex-1">
        <div className="flex min-h-[20px] items-center gap-1.5">
          <b className="min-w-0 truncate text-[12.5px] font-bold text-text">{message.author.name}</b>
          <span className="shrink-0 text-[11px] text-text-faint">
            {message.when}{message.edited ? ' · edited' : ''}
          </span>
          {message.mine && !editing && (
            <button ref={more} type="button" aria-label="Message options"
              onClick={() => { if (more.current) onMenu(more.current) }}
              className={cn('ml-auto inline-flex shrink-0 items-center justify-center rounded-md text-text-faint hover:bg-hover hover:text-text-muted',
                phone ? 'h-9 w-9' : 'h-6 w-6')}>
              <Glyph name="more" size={15} />
            </button>
          )}
        </div>
        {editing ? (
          <div className="mt-1">
            <textarea
              ref={box}
              aria-label="Edit message"
              value={draft}
              maxLength={TASK_CHAT_MAX_CHARS}
              rows={Math.min(8, Math.max(2, draft.split('\n').length))}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') { event.stopPropagation(); onCancel() }
                if (event.key === 'Enter' && !event.shiftKey && !phone && !event.nativeEvent.isComposing) {
                  event.preventDefault()
                  void save()
                }
              }}
              className="block w-full resize-y rounded-[10px] border border-accent bg-surface px-3 py-2 text-[13px] leading-[1.45] text-text outline-none"
            />
            <div className="mt-1.5 flex items-center justify-end gap-1.5">
              <button type="button" onClick={onCancel}
                className="inline-flex h-8 items-center rounded-lg border border-line bg-surface px-3 text-[12.5px] font-semibold hover:bg-hover">
                Cancel
              </button>
              <button type="button" onClick={() => void save()} disabled={!draft.trim()}
                className="inline-flex h-8 items-center rounded-lg border border-accent bg-accent px-3 text-[12.5px] font-semibold text-white hover:bg-accent-dark disabled:border-line disabled:bg-surface disabled:text-text-faint">
                Save
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-0.5 whitespace-pre-wrap break-words text-[13px] leading-[1.5] text-text-body">
            <ChatText body={message.body} />
          </p>
        )}
      </div>
    </li>
  )
}

/** The ⋯ of one's own message: a menu under it on a computer, a sheet from the bottom on a phone. */
function MessageMenu({ anchor, onClose, onEdit, onRemove }: {
  anchor: HTMLElement | null; onClose: () => void; onEdit: () => void; onRemove: () => void
}) {
  const phone = usePhone()
  const row = (glyph: 'pencil' | 'close', label: string, run: () => void, danger = false): ReactNode => (
    <button type="button" onClick={run}
      className={cn('flex w-full items-center gap-2.5 px-3.5 text-left font-medium hover:bg-hover',
        phone ? 'h-[52px] text-[15px]' : 'h-9 text-[13px]', danger ? 'text-danger' : 'text-text')}>
      <Glyph name={glyph} size={phone ? 17 : 14} />{label}
    </button>
  )
  const rows = (
    <>
      {row('pencil', 'Edit message', onEdit)}
      {row('close', 'Remove message', onRemove, true)}
    </>
  )
  if (phone) {
    return (
      <>
        {/* Back closes the menu before the chat (Aaron, Oct 26). */}
        <BackLayer onBack={onClose} />
        <PanelSheet open onClose={onClose} title="Your message" layer={70}><div className="py-1">{rows}</div></PanelSheet>
      </>
    )
  }
  return (
    <Popover open onClose={onClose} anchor={anchor} width={200} align="right" label="Message options"
      className="rounded-xl py-1" layer={70}>
      {rows}
    </Popover>
  )
}

/**
 * The chat itself: beside the sheet (`order-first` in its row, so it stands
 * to the sheet's left) on a computer and an iPad, the whole screen with
 * **‹ Task** on a phone.
 */
export function TaskChatPanel({ chat, onCloseSheet }: { chat: TaskChat; onCloseSheet: () => void }) {
  const phone = usePhone()
  const scroller = useRef<HTMLDivElement>(null)
  const atFoot = useRef(true)
  const [menu, setMenu] = useState<{ id: string; anchor: HTMLElement } | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const items = chat.answer?.items ?? []
  const last = items[items.length - 1]?.id ?? null

  /* The newest is at the foot: there on opening, and kept there while somebody is reading the foot. */
  useLayoutEffect(() => {
    const el = scroller.current
    if (el && atFoot.current) el.scrollTop = el.scrollHeight
  }, [last, chat.answer])

  const onScroll = () => {
    const el = scroller.current
    if (el) atFoot.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48
  }

  const send = async (body: string) => {
    atFoot.current = true
    return chat.send(body)
  }

  const close = () => chat.setOpen(false)

  const head = phone ? (
    <header className="flex min-h-[56px] shrink-0 items-center gap-2.5 border-b border-line-faint px-4 py-2">
      <button type="button" onClick={close}
        className="-ml-2 flex h-9 shrink-0 items-center gap-0.5 rounded-button pl-1.5 pr-2 text-cell font-semibold text-accent">
        <IconChevronLeft size={15} />Task
      </button>
      <span className="flex min-w-0 flex-col">
        <b className="truncate text-[17px] font-bold leading-tight text-text">Chat</b>
        <span className="truncate text-[12px] leading-tight text-text-muted">{countWords(chat.count)}</span>
      </span>
      <button type="button" aria-label="Close" onClick={onCloseSheet}
        className="ml-auto flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-chip bg-hover text-text-muted">
        <Glyph name="close" size={17} />
      </button>
    </header>
  ) : (
    <header className="flex shrink-0 items-center gap-2.5 border-b border-line-faint px-4 py-3.5">
      <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg border border-line bg-surface-alt text-text-muted">
        <Glyph name="bubble" size={16} />
      </span>
      <div className="flex min-w-0 flex-col">
        <b className="text-[14.5px]">Chat</b>
        <span className="truncate text-[11.5px] text-text-muted">{countWords(chat.count)}</span>
      </div>
      <button type="button" aria-label="Close chat" onClick={close}
        className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-hover">
        <Glyph name="close" size={16} />
      </button>
    </header>
  )

  const body = chat.answer === null ? (
    <div className="p-4 text-[13px] text-text-muted">{chat.error ?? 'Loading…'}</div>
  ) : items.length === 0 ? (
    <div className="flex h-full flex-col items-center justify-center gap-1.5 px-8 py-10 text-center">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-surface-alt text-text-faint">
        <Glyph name="bubble" size={18} />
      </span>
      <b className="text-[13px] font-semibold text-text">No messages yet</b>
      <span className="text-[12px] leading-[1.45] text-text-muted">
        Notes for everyone who can see this task, with who wrote each one and when.
      </span>
    </div>
  ) : (
    <ol aria-label="Messages" className="py-1.5">
      {items.map((message) => (
        <Message
          key={message.id}
          message={message}
          editing={editing === message.id}
          onMenu={(anchor) => setMenu({ id: message.id, anchor })}
          onSave={(next) => chat.edit(message.id, next)}
          onCancel={() => setEditing(null)}
        />
      ))}
    </ol>
  )

  return (
    <section
      aria-label="Chat"
      onKeyDown={(event) => {
        /*
         * Escape inside the chat is the chat's: it shuts the menu or the
         * confirm on top, else the chat — never the task behind it, whose
         * own listener is on the window and would otherwise hear it too.
         */
        if (event.key !== 'Escape') return
        event.stopPropagation()
        if (menu) setMenu(null)
        else if (removing) setRemoving(null)
        else close()
      }}
      className={cn('flex flex-col bg-surface',
        phone ? 'fixed inset-0 z-10' : 'order-first h-full w-[340px] min-w-0 max-w-[calc(100%-480px)] border-l border-line shadow-slideover')}
    >
      {head}
      <div ref={scroller} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto">{body}</div>
      <footer className={cn('shrink-0 border-t border-line bg-surface-alt px-3 py-3', phone && 'pb-safe')}>
        <Composer onSend={send} focus={chat.pressed && !phone} />
        {chat.error && chat.answer !== null && <div className="mt-1.5 px-1 text-[12px] text-danger">{chat.error}</div>}
      </footer>
      {menu && (
        <MessageMenu
          anchor={menu.anchor}
          onClose={() => setMenu(null)}
          onEdit={() => { setEditing(menu.id); setMenu(null) }}
          onRemove={() => { setRemoving(menu.id); setMenu(null) }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Remove this message?"
          body="It comes off the chat for everyone on this task. Its history stays."
          confirmLabel="Remove"
          tone="danger"
          layer={70}
          onCancel={() => setRemoving(null)}
          onConfirm={() => { const id = removing; setRemoving(null); void chat.remove(id) }}
        />
      )}
    </section>
  )
}
